/**
 * A project's Markdown file rendered as GitHub/GitLab show it: GFM (tables, task lists, strikethrough, autolinks,
 * footnotes), headings with GitHub's ids (`#getting-started` scrolls to it), fenced code highlighted (the chat's
 * CodeBlock), ```mermaid blocks drawn as diagrams (src/Mermaid.jsx, loaded only when a file has one; a broken diagram
 * shows its error and source), relative links and images resolved inside the project (an image is read from the
 * project, a link opens that file through `onOpenFile`), external links in a new tab. No raw HTML: nothing in the file
 * runs on Flow's page. Used by the Home's File gadget, the Code tab's Markdown preview and the Files tab's preview.
 */
import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as vfsClient from '../../utils/vfsClient';
import { mimeTypeFor } from '../../utils/binaryFiles';
import { createSlugger, isExternalUrl, resolveProjectPath } from '../../utils/markdownLinks';
import './MarkdownView.css';

const Mermaid = lazy(() => import('../../Mermaid'));
const CodeBlock = lazy(() => import('../CodeBlock'));

const REMARK_PLUGINS = [remarkGfm];
// react-markdown's own prefix for footnote ids; headings use it too, as on GitHub, so ids never clash with Flow's.
const ID_PREFIX = 'user-content-';
// Languages that are not code: shown as a plain block, not through the highlighter.
const PLAIN = new Set(['', 'text', 'txt', 'plain', 'plaintext', 'console', 'output']);

/** The text of a hast node (a heading, a code block). */
const textOf = (node) => {
  if (!node) { return ''; }
  if (node.type === 'text') { return node.value || ''; }
  return (node.children || []).map(textOf).join('');
};

/** Gives each heading GitHub's id (`user-content-getting-started`), once per parse, so links to `#getting-started` work. */
const rehypeHeadingIds = () => (tree) => {
  const slug = createSlugger();
  const walk = (node) => {
    if (node.type === 'element' && /^h[1-6]$/.test(node.tagName)) {
      node.properties = { ...node.properties, id: `${ID_PREFIX}${slug(textOf(node))}` };
      return;
    }
    (node.children || []).forEach(walk);
  };
  walk(tree);
};
const REHYPE_PLUGINS = [rehypeHeadingIds];

/** An image of the project, read from vfs-service (a blob URL while shown); its alt text while loading or if missing. */
function ProjectImage({ projectId, path, alt = '', title }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    let url = null;
    setSrc(null);
    setFailed(false);
    Promise.resolve().then(() => vfsClient.readBinaryFile(projectId, path, mimeTypeFor(path.split('/').pop()))).then((blob) => {
      if (!alive) { return; }
      if (!blob) { setFailed(true); return; }
      url = URL.createObjectURL(blob);
      setSrc(url);
    }).catch(() => { if (alive) { setFailed(true); } });
    return () => {
      alive = false;
      if (url) { URL.revokeObjectURL(url); }
    };
  }, [projectId, path]);
  if (failed) { return <span className="md-image-missing" title={`${path} could not be read`}>{alt || path}</span>; }
  if (!src) { return <span className="md-image-loading" aria-busy="true">{alt}</span>; }
  return <img alt={alt} src={src} title={title} />;
}

ProjectImage.propTypes = {
  alt: PropTypes.string,
  path: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  title: PropTypes.string,
};

export default function MarkdownView({ source, projectId = null, filePath = '', onOpenFile = null, className = '', testId = undefined, tableClassName = undefined }) {
  const rootRef = useRef(null);
  // The latest onOpenFile, read when a link is clicked: a new callback each render (an inline arrow) does not make new
  // components, which would remount the diagrams and read the images again.
  const openRef = useRef(onOpenFile);
  openRef.current = onOpenFile;
  const canOpen = Boolean(onOpenFile);

  // Scrolls to a heading (or footnote) of this document: `#slug` as GitHub writes it, with or without the prefix.
  const scrollTo = (hash) => {
    const root = rootRef.current;
    if (!root || !hash) { return; }
    let wanted = hash;
    try { wanted = decodeURIComponent(hash); } catch { /* as written */ }
    const target = [...root.querySelectorAll('[id]')].find((el) => el.id === `${ID_PREFIX}${wanted}` || el.id === wanted);
    target?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  };

  const components = useMemo(() => {
    const Link = ({ href = '', children, title }) => {
      if (href.startsWith('#')) {
        return <a href={href} onClick={(event) => { event.preventDefault(); scrollTo(href.slice(1)); }} title={title}>{children}</a>;
      }
      if (!href || isExternalUrl(href)) {
        return <a href={href || undefined} rel="noopener noreferrer" target="_blank" title={title}>{children}</a>;
      }
      const target = resolveProjectPath(filePath, href);
      if (!target || !target.path || target.path.endsWith('/') || !canOpen) {
        // A folder, the project root or a link with nowhere to go here: its text, saying where it points.
        return <span className="md-link-inert" title={target?.path || href}>{children}</span>;
      }
      return (
        <a
          className="md-link-file"
          href={`#${encodeURI(target.path)}`}
          onClick={(event) => { event.preventDefault(); openRef.current?.(target.path, target.hash || null); }}
          title={title || `Open ${target.path}`}
        >
          {children}
        </a>
      );
    };
    Link.propTypes = { children: PropTypes.node, href: PropTypes.string, title: PropTypes.string };
    const Image = ({ src = '', alt = '', title }) => {
      if (!src) { return null; }
      if (isExternalUrl(src)) { return <img alt={alt} loading="lazy" referrerPolicy="no-referrer" src={src} title={title} />; }
      const target = projectId ? resolveProjectPath(filePath, src) : null;
      if (!target?.path) { return <span className="md-image-missing">{alt || src}</span>; }
      return <ProjectImage alt={alt} path={target.path} projectId={projectId} title={title} />;
    };
    Image.propTypes = { alt: PropTypes.string, src: PropTypes.string, title: PropTypes.string };
    // A fenced block: ```mermaid as a diagram, a language highlighted, anything else as it is.
    const Pre = ({ node, children }) => {
      const code = (node?.children || []).find((child) => child.tagName === 'code');
      const classes = [].concat(code?.properties?.className || []);
      const language = (classes.find((name) => String(name).startsWith('language-')) || '').slice('language-'.length).toLowerCase();
      const text = textOf(code).replace(/\n$/, '');
      const plain = <pre><code>{text}</code></pre>;
      if (language === 'mermaid') {
        return (
          <div className="md-mermaid" data-testid="md-mermaid">
            <Suspense fallback={<pre className="md-mermaid-loading"><code>{text}</code></pre>}>
              <Mermaid chart={text} fromFile />
            </Suspense>
          </div>
        );
      }
      if (!code) { return <pre>{children}</pre>; }
      if (PLAIN.has(language)) { return plain; }
      return <div className="md-code"><Suspense fallback={plain}><CodeBlock className={`language-${language}`}>{text}</CodeBlock></Suspense></div>;
    };
    Pre.propTypes = { children: PropTypes.node, node: PropTypes.object };
    const Table = ({ children }) => <div className="md-table-scroll"><table className={tableClassName}>{children}</table></div>;
    Table.propTypes = { children: PropTypes.node };
    return {
      a: Link,
      img: Image,
      pre: Pre,
      table: Table,
    };
  // scrollTo and the links only read refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, filePath, canOpen, tableClassName]);

  return (
    <div className={`md-view ${className}`.trim()} data-testid={testId} ref={rootRef}>
      <ReactMarkdown components={components} rehypePlugins={REHYPE_PLUGINS} remarkPlugins={REMARK_PLUGINS}>{source || ''}</ReactMarkdown>
    </div>
  );
}

MarkdownView.propTypes = {
  className: PropTypes.string,
  // The file's path in the project (root-relative): relative links and images resolve from its folder.
  filePath: PropTypes.string,
  // Opens another project file `(path, hash)`; none: a relative link shows as text.
  onOpenFile: PropTypes.func,
  // Reads the file's relative images; none: they show their alt text.
  projectId: PropTypes.string,
  source: PropTypes.string,
  // A class for tables (the Code preview's `markdown-table` styles).
  tableClassName: PropTypes.string,
  testId: PropTypes.string,
};
